import { useState } from 'react';

const CTA = () => {
  const [formData, setFormData] = useState({
    name: '',
    company: '',
    email: '',
    phone: '',
    eventType: '',
    otherEventType: '',
    budget: '',
    eventDate: '',
    message: '',
  });
  const [submitted, setSubmitted] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  const handleChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) {
      setError('お名前、メールアドレス、メッセージは必須項目です。');
      return;
    }
    setError('');
    setSubmitting(true);
    try {
      const body = new URLSearchParams();
      Object.entries(formData).forEach(([key, value]) => {
        if (value) body.append(key, value);
      });
      const res = await fetch('https://readdy.ai/api/form/d6jhde2paf6ievb71frg', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (res.ok) {
        setSubmitted(true);
      } else {
        setError('送信に失敗しました。もう一度お試しください。');
      }
    } catch {
      setError('送信に失敗しました。もう一度お試しください。');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section id="contact" className="py-32 text-white" style={{ backgroundColor: '#9E9294' }}>
      <div className="max-w-7xl mx-auto px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-start">

          {/* Left: Text + Photo */}
          <div className="space-y-10 pt-4">
            <div className="space-y-6">
              <h2 className="font-extralight leading-tight tracking-tight" style={{ fontSize: 'clamp(1.8rem, 3.8vw, 3.4rem)' }}>
                プロジェクトを
                <br />
                始めましょう
              </h2>
              <p className="text-base font-light leading-relaxed max-w-md" style={{ color: 'rgb(200, 16, 46)' }}>
                あなたのビジョンを、私たちの専門知識で
                <br />
                形にします。まずはお気軽にご相談ください。
              </p>
            </div>
            <div className="space-y-4 text-base font-light text-white/70">
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-mail-line text-base"></i>
                </div>
                <span>bytwotempos@gmail.com</span>
              </div>
              <div className="flex items-center space-x-3">
                <div className="w-5 h-5 flex items-center justify-center">
                  <i className="ri-time-line text-base"></i>
                </div>
                <span>平日 10:00 – 18:00</span>
              </div>
            </div>
          </div>

          {/* Right: Form */}
          <div>
            {submitted ? (
              <div className="flex flex-col items-center justify-center py-20 space-y-4 text-center">
                <div className="w-14 h-14 flex items-center justify-center rounded-full border border-white/40">
                  <i className="ri-check-line text-2xl"></i>
                </div>
                <p className="text-xl font-light tracking-wide">お問い合わせを受け付けました。</p>
                <p className="text-sm text-white/60">近日中にご連絡いたします。</p>
              </div>
            ) : (
              <form
                data-readdy-form
                id="contact-form"
                onSubmit={handleSubmit}
                className="space-y-5"
              >
                {/* Name + Company */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">お名前 *</label>
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                      placeholder="山田 太郎"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">会社名</label>
                    <input
                      type="text"
                      name="company"
                      value={formData.company}
                      onChange={handleChange}
                      placeholder="株式会社〇〇"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Email + Phone */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">メールアドレス *</label>
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                      placeholder="example@email.com"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200"
                    />
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">電話番号</label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                      placeholder="090-0000-0000"
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200"
                    />
                  </div>
                </div>

                {/* Event Type + Budget */}
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">イベントの種類</label>
                    <select
                      name="eventType"
                      value={formData.eventType}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white transition-colors duration-200 cursor-pointer appearance-none"
                      style={{ backgroundColor: '#9E9294' }}
                    >
                      <option value="" disabled style={{ backgroundColor: '#9E9294' }}>選択してください</option>
                      <option value="ウェディング" style={{ backgroundColor: '#9E9294' }}>ウェディング</option>
                      <option value="コーポレートイベント" style={{ backgroundColor: '#9E9294' }}>コーポレートイベント</option>
                      <option value="プライベートパーティー" style={{ backgroundColor: '#9E9294' }}>プライベートパーティー</option>
                      <option value="ガラディナー" style={{ backgroundColor: '#9E9294' }}>ガラディナー</option>
                      <option value="その他" style={{ backgroundColor: '#9E9294' }}>その他</option>
                    </select>
                    {formData.eventType === 'その他' && (
                      <div className="mt-3 space-y-1">
                        <label className="text-xs tracking-widest text-white/60 uppercase">内容をご記入ください</label>
                        <input
                          type="text"
                          name="otherEventType"
                          value={formData.otherEventType}
                          onChange={handleChange}
                          placeholder="例：卒業パーティー、同窓会など"
                          className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200"
                        />
                      </div>
                    )}
                  </div>
                  <div className="space-y-1">
                    <label className="text-xs tracking-widest text-white/60 uppercase">ご予算</label>
                    <select
                      name="budget"
                      value={formData.budget}
                      onChange={handleChange}
                      className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white transition-colors duration-200 cursor-pointer appearance-none"
                      style={{ backgroundColor: '#9E9294' }}
                    >
                      <option value="" disabled style={{ backgroundColor: '#9E9294' }}>選択してください</option>
                      <option value="〜100万円" style={{ backgroundColor: '#9E9294' }}>〜100万円</option>
                      <option value="100〜300万円" style={{ backgroundColor: '#9E9294' }}>100〜300万円</option>
                      <option value="300〜500万円" style={{ backgroundColor: '#9E9294' }}>300〜500万円</option>
                      <option value="500万円以上" style={{ backgroundColor: '#9E9294' }}>500万円以上</option>
                      <option value="未定" style={{ backgroundColor: '#9E9294' }}>未定</option>
                    </select>
                  </div>
                </div>

                {/* Event Date */}
                <div className="space-y-1">
                  <label className="text-xs tracking-widest text-white/60 uppercase">開催予定日</label>
                  <input
                    type="date"
                    name="eventDate"
                    value={formData.eventDate}
                    onChange={handleChange}
                    className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-white transition-colors duration-200"
                    style={{ colorScheme: 'dark', fontSize: '1rem', WebkitAppearance: 'none' }}
                  />
                  <style>{`
                    input[type="date"]::-webkit-calendar-picker-indicator {
                      width: 22px;
                      height: 22px;
                      cursor: pointer;
                      filter: invert(1);
                      opacity: 0.8;
                    }
                  `}</style>
                </div>

                {/* Message */}
                <div className="space-y-1">
                  <label className="text-xs tracking-widest text-white/60 uppercase">ご要望・メッセージ *</label>
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                    placeholder="イベントの詳細やご要望をお聞かせください。"
                    rows={4}
                    maxLength={500}
                    className="w-full bg-transparent border-b border-white/30 focus:border-white outline-none py-2 text-sm text-white placeholder-white/30 transition-colors duration-200 resize-none"
                  />
                  <p className="text-xs text-white/40 text-right">{formData.message.length}/500</p>
                </div>

                {error && (
                  <p className="text-sm" style={{ color: 'rgb(200, 16, 46)' }}>{error}</p>
                )}

                <button
                  type="submit"
                  disabled={submitting}
                  className="group relative mt-2 px-6 py-3 md:px-10 md:py-5 cursor-pointer whitespace-nowrap overflow-hidden disabled:opacity-50"
                >
                  <span className="relative z-10 flex items-center space-x-3 text-white group-hover:text-white transition-colors duration-300">
                    <span className="text-sm tracking-wide">{submitting ? '送信中...' : '送信する'}</span>
                    <i className="ri-arrow-right-line text-lg transition-transform group-hover:translate-x-2 duration-300"></i>
                  </span>
                  <div className="absolute inset-0 border border-white"></div>
                  <div className="absolute inset-0 scale-x-0 group-hover:scale-x-100 transition-transform duration-300 origin-left" style={{ backgroundColor: 'rgb(200, 16, 46)' }}></div>
                </button>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};

export default CTA;
